import React from 'react';
import { Theme, useTheme } from '@fluentui/react';
import { HealthStatus, Pipeline, WorkLane } from '../models/CloudStatusModels';

// ===== Color and Icon Helpers =====
export const getStatusColor = (status: HealthStatus): string => {
    switch (status) {
        case 'Healthy':
            return '#107C10';
        case 'Degraded':
            return '#FFC83D';
        case 'Unhealthy':
            return '#D13438';
        case 'Unknown':
            return '#8A8886';
        case 'Disabled':
            return '#8764B8'; // Purple
        case 'Ignored':
            return '#CA8B04'; // Golden Yellow
    }
};

export const getStatusIcon = (status: HealthStatus): string => {
    switch (status) {
        case 'Healthy':
            return '✓';
        case 'Degraded':
            return '!';
        case 'Unhealthy':
            return '✗';
        case 'Unknown':
            return '?';
        case 'Disabled':
            return '⊘';
        case 'Ignored':
            return '○';
    }
};

// Fluent UI icon names for status
export const getStatusFluentIcon = (status: HealthStatus): string => {
    switch (status) {
        case 'Healthy':
            return 'CompletedSolid';
        case 'Degraded':
            return 'WarningSolid';
        case 'Unhealthy':
            return 'StatusErrorFull';
        case 'Unknown':
            return 'Unknown';
        case 'Disabled':
            return 'Blocked2Solid';
        case 'Ignored':
            return 'StatusCircleBlock';
    }
};

// ===== Date Formatting Helpers =====
export const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

export const formatDateTime = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(date);
};

// ===== Pipeline Helpers =====
export const getPipelineName = (pipeline: Pipeline): string => {
    switch (pipeline) {
        case Pipeline.Blob:
            return 'Data';
        case Pipeline.Core:
            return 'Dialtone';
        case Pipeline.Message:
            return 'Message';
        default:
            return 'Unknown';
    }
};

export const getWorkLaneName = (workLane: WorkLane): string => {
    switch (workLane) {
        case WorkLane.Warm:
            return 'Normal';
        case WorkLane.Dcmt:
            return 'Priority';
        default:
            return 'Unknown';
    }
};

export const getRegionName = (region: number): string => {
    return region === 0 ? 'Primary' : 'Secondary';
};

// ===== Responsive Helpers =====
export const isMobileWidth = (): boolean => {
    return window.innerWidth <= 768;
};

export const isSmallMobileWidth = (): boolean => {
    return window.innerWidth <= 640;
};

// ===== Style Helpers =====
export const getStatusBannerStyle = (status: HealthStatus) => {
    const color = getStatusColor(status);
    return {
        width: '100%',
        backgroundColor: color + '08',
        borderRadius: '4px',
        border: `2px solid ${color}`,
        padding: '16px 24px',
        boxShadow:
            '0 1.6px 3.6px 0 rgba(0, 0, 0, .132), 0 .3px .9px 0 rgba(0, 0, 0, .108)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
};

export const getCardStyle = (theme?: Theme) => ({
    backgroundColor: theme ? theme.semanticColors.bodyBackground : '#ffffff',
    borderRadius: '4px',
    boxShadow:
        '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
});

export const getHeaderStyle = (
    themeDark: string,
    isMobile: boolean = false,
    theme?: Theme
) => ({
    padding: isMobile ? '16px' : '16px 20px',
    backgroundColor: theme ? theme.semanticColors.bodyBackground : '#ffffff',
    borderBottom: `2px solid ${themeDark}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: isMobile ? ('wrap' as const) : ('nowrap' as const),
});

// ===== React Components =====
export const StageCircle: React.FC<{ stage: any }> = ({ stage }) => {
    const theme = useTheme();
    const color = getStatusColor(stage.status);

    return (
        <div
            style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: color,
                border: `2px solid ${color}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: theme.effects.elevation4,
                margin: '0 8px',
            }}
            title={`${stage.name}: ${stage.status}`}
        >
            <span
                style={{
                    fontSize: '12px',
                    color: 'white',
                    fontWeight: 'bold',
                    lineHeight: '1',
                }}
            >
                {getStatusIcon(stage.status)}
            </span>
        </div>
    );
};

export const ConnectionLine: React.FC<{ connection: any }> = ({
    connection,
}) => {
    const color = getStatusColor(connection.status);

    return (
        <div
            style={{
                width: '12px',
                height: '2px',
                backgroundColor: color,
                display: 'inline-block',
                margin: '0 2px',
            }}
            title={`Connection: ${connection.status}`}
        />
    );
};
